import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { ChartBar, Clock, Rows, Calendar as CalendarIcon } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdminMovilizacionCard } from '@/components/AdminMovilizacionCard';
import { ExportReportDialog } from '@/components/ExportReportDialog';
import { CalendarView } from '@/components/CalendarView';
import { Button } from '@/components/ui/button';
import { LoadingOrEmpty } from '@/components/LoadingOrEmpty';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPage = () => {
  const [movilizaciones, setMovilizaciones] = useState([]);
  const [calendario, setCalendario] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todas');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'

  const fetchData = useCallback(async () => {
    try {
      const [movilizacionesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/movilizaciones`, { withCredentials: true }),
        axios.get(`${API_URL}/api/stats`, { withCredentials: true })
      ]);
      setMovilizaciones(movilizacionesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del panel');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendario = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/calendario`, { withCredentials: true });
      setCalendario(data);
    } catch (error) {
      // silent — calendar is a secondary view
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCalendario();
  }, [fetchData, fetchCalendario]);

  useEffect(() => {
    if (viewMode !== 'calendar') return undefined;
    const id = setInterval(fetchCalendario, 30000);
    return () => clearInterval(id);
  }, [viewMode, fetchCalendario]);

  const filterByEstado = (estado) => {
    if (estado === 'todas') return movilizaciones;
    return movilizaciones.filter((m) => m.estado === estado);
  };

  const pendingCount = movilizaciones.filter(
    (m) => m.estado === 'agendado'
  ).length;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A] mb-2">
                Panel Administrativo
              </h1>
              <p className="text-[#495057] font-['IBM_Plex_Sans']">
                Gestiona todas las solicitudes de movilización
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex border border-[#E9ECEF] bg-white">
                <Button
                  data-testid="view-list-button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`gap-2 rounded-none font-['IBM_Plex_Sans'] h-12 px-4 transition-colors ${
                    viewMode === 'list' ? 'bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] hover:text-white' : 'hover:bg-[#F8F9FA]'
                  }`}
                >
                  <Rows size={16} weight="bold" /> Lista
                </Button>
                <Button
                  data-testid="view-calendar-button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className={`gap-2 rounded-none font-['IBM_Plex_Sans'] h-12 px-4 transition-colors ${
                    viewMode === 'calendar' ? 'bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] hover:text-white' : 'hover:bg-[#F8F9FA]'
                  }`}
                >
                  <CalendarIcon size={16} weight="bold" /> Calendario
                </Button>
              </div>
              <ExportReportDialog />
            </div>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-6 bg-white border border-[#E9ECEF]">
                <p className="text-[#495057] text-sm font-['IBM_Plex_Sans'] mb-2">Total</p>
                <p className="text-3xl font-black font-['Chivo'] text-[#0A0A0A]">{stats.total}</p>
              </div>
              <div className="p-6 bg-white border border-[#E9ECEF]">
                <p className="text-[#6C757D] text-sm font-['IBM_Plex_Sans'] mb-2">Agendadas</p>
                <p className="text-3xl font-black font-['Chivo'] text-[#6C757D]">{stats.agendadas}</p>
              </div>
              <div className="p-6 bg-white border border-[#E9ECEF]">
                <p className="text-[#0891B2] text-sm font-['IBM_Plex_Sans'] mb-2">En Espera</p>
                <p className="text-3xl font-black font-['Chivo'] text-[#0891B2]">{stats.en_espera_asignacion || 0}</p>
              </div>
              <div className="p-6 bg-white border border-[#E9ECEF]">
                <p className="text-[#008630] text-sm font-['IBM_Plex_Sans'] mb-2">Asignadas</p>
                <p className="text-3xl font-black font-['Chivo'] text-[#008630]">{stats.asignadas || 0}</p>
              </div>
              <div className="p-6 bg-white border border-[#E9ECEF]">
                <p className="text-[#FF3B30] text-sm font-['IBM_Plex_Sans'] mb-2">Rechazadas</p>
                <p className="text-3xl font-black font-['Chivo'] text-[#FF3B30]">{stats.rechazadas}</p>
              </div>
            </div>
          )}

          {pendingCount > 0 && (
            <div className="mt-4 p-4 bg-[#F5A623]/10 border border-[#F5A623] flex items-center gap-3">
              <Clock size={24} weight="bold" className="text-[#F5A623]" />
              <p className="font-['IBM_Plex_Sans'] text-[#0A0A0A]">
                <span className="font-bold">{pendingCount}</span> solicitudes pendientes de revisión
              </p>
            </div>
          )}
        </div>

        {viewMode === 'calendar' ? (
          <CalendarView movilizaciones={calendario} />
        ) : (
        /* Tabs */
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-[#E9ECEF] p-1 h-auto inline-flex">
            <TabsTrigger
              value="todas"
              data-testid="admin-tab-todas"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Todas ({stats?.total || 0})
            </TabsTrigger>
            <TabsTrigger
              value="agendado"
              data-testid="admin-tab-agendado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#6C757D] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Agendadas ({stats?.agendadas || 0})
            </TabsTrigger>
            <TabsTrigger
              value="en_espera_asignacion"
              data-testid="admin-tab-en-espera"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#0891B2] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              En Espera ({stats?.en_espera_asignacion || 0})
            </TabsTrigger>
            <TabsTrigger
              value="asignado"
              data-testid="admin-tab-asignado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#008630] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Asignadas ({stats?.asignadas || 0})
            </TabsTrigger>
            <TabsTrigger
              value="realizada"
              data-testid="admin-tab-realizada"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#065F46] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Realizadas ({stats?.realizadas || 0})
            </TabsTrigger>
            <TabsTrigger
              value="suspendida"
              data-testid="admin-tab-suspendida"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#78716C] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Suspendidas ({stats?.suspendidas || 0})
            </TabsTrigger>
            <TabsTrigger
              value="rechazado"
              data-testid="admin-tab-rechazado"
              className="font-['IBM_Plex_Sans'] font-medium data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white h-auto py-2.5 px-5"
            >
              Rechazadas ({stats?.rechazadas || 0})
            </TabsTrigger>
          </TabsList>

          {['todas', 'agendado', 'en_espera_asignacion', 'asignado', 'realizada', 'suspendida', 'rechazado'].map((estado) => {
            const filtered = filterByEstado(estado);
            const emptyMsg = estado === 'todas'
              ? 'No hay movilizaciones'
              : `No hay movilizaciones en estado "${estado}"`;
            return (
              <TabsContent key={estado} value={estado} className="mt-6">
                <LoadingOrEmpty
                  loading={loading}
                  isEmpty={filtered.length === 0}
                  emptyIcon={<ChartBar size={48} className="text-[#E9ECEF] mx-auto mb-4" weight="bold" />}
                  emptyMessage={emptyMsg}
                  testId="admin"
                >
                  <div className="grid grid-cols-1 gap-4">
                    {filtered.map((mov) => (
                      <AdminMovilizacionCard
                        key={mov.id}
                        movilizacion={mov}
                        onUpdate={fetchData}
                      />
                    ))}
                  </div>
                </LoadingOrEmpty>
              </TabsContent>
            );
          })}
        </Tabs>
        )}
      </div>
    </div>
  );
};